/**
 * ProductTypeDetail - Redesigned Product Type Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Collapsible sections
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageField } from '../../components/container';
import { ImageBox } from '../../components/base/ImageBox';
import { slugHelper } from '../../utils/slugHelper';
import { dfProductTypeModel, ProductTypeModel, validProductTypeSchema } from '../../context/models/product-type/ProductTypeModel';
import { Type, Image as ImageIcon, Link as LinkIcon, Settings } from 'lucide-react';

type IProductTypeDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: ProductTypeModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: ProductTypeModel) => void;
};

const ProductTypeDetail = (props: IProductTypeDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<ProductTypeModel>(dfProductTypeModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<ProductTypeModel>({
		resolver: yupResolver(validProductTypeSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: ProductTypeModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof ProductTypeModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Basic Info
	const basicInfoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Tên chuyên mục'
				required
				error={errors.name?.message}
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên chuyên mục...'
				/>
			</EditPageField>

			<EditPageField
				label='Đường dẫn'
				required
				error={errors.url?.message}
				helpText='URL của trang loại sản phẩm'
			>
				<TextBox
					name='url'
					error={errors.url?.message}
					onValueChanged={(value) => updateField('url', value)}
					value={initialValues.url}
					hasValid={true}
					placeholder='https://...'
				/>
			</EditPageField>

			<EditPageField
				label='Số thứ tự'
				error={errors.stt?.message}
				helpText='Thứ tự hiển thị trong danh sách'
			>
				<NumberBox
					name='stt'
					error={errors.stt?.message}
					onValueChanged={(value) => updateField('stt', value)}
					value={initialValues.stt}
					hasValid={true}
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Media
	const mediaTab = (
		<div className='space-y-6'>
			<EditPageField
				label='Ảnh nền'
				error={errors.background?.message}
				helpText='Hình nền hiển thị cho loại sản phẩm'
			>
				<ImageBox
					name='background'
					isDisabled={initialValues.name.length <= 0}
					file_name={initialValues.name}
					value={initialValues.background}
					onDataChanged={(background) => {
						updateField('background', background?.path || '');
					}}
				/>
			</EditPageField>

			<EditPageField
				label='Icon'
				error={errors.icon?.message}
				helpText='Icon thu nhỏ cho loại sản phẩm'
			>
				<ImageBox
					name='icon'
					isDisabled={initialValues.name.length <= 0}
					file_name={initialValues.name}
					value={initialValues.icon}
					onDataChanged={(icon) => {
						updateField('icon', icon?.path || '');
					}}
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'basic',
			label: 'Thông tin cơ bản',
			icon: <Type className='h-4 w-4' />,
			content: basicInfoTab,
		},
		{
			id: 'media',
			label: 'Hình ảnh',
			icon: <ImageIcon className='h-4 w-4' />,
			content: mediaTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới loại sản phẩm' : 'Chỉnh sửa loại sản phẩm'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Loại sản phẩm', href: '/product-type' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default ProductTypeDetail;
